/* global $ */

import { KEYCODE_BACKSPACE, KEYCODE_Y, KEYCODE_Z } from '@aytm/lib/common/const'
import { scrollX, scrollY } from '@aytm/lib/layout/scroll'
export { scrollX, scrollY }

export const IS_IE = 'undefined' !== typeof window && /MSIE|Trident\/|Edge\//.test(window.navigator.userAgent)

/* compat/compat disabled because usage of CSS.supports is conditional */
export const CSS_SUPPORTS_GRID =
  typeof CSS != 'undefined' && 'function' == typeof CSS.supports && CSS.supports('display', 'grid')
export const CSS_SUPPORTS_STICKY =
  typeof CSS != 'undefined' && 'function' == typeof CSS.supports && CSS.supports('position', 'sticky')

export function stopEvent(event) {
  if ('object' == typeof event) {
    try {
      if (event.stopPropagation) event.stopPropagation()
    } catch {
      // skip
    }
    try {
      if (event.preventDefault) event.preventDefault()
    } catch {
      // skip
    }
    try {
      if (event.stopImmediatePropagation) event.stopImmediatePropagation()
    } catch {
      // skip
    }
  }
  return event
}

export function stopDragOver(event) {
  stopEvent(event)
  if (event && event.dataTransfer) {
    event.dataTransfer.dropEffect = 'none'
  }
}

export const timestamp =
  'function' == typeof Date.now
    ? Date.now
    : function () {
        return Date.now()
      }

export function throttle(fn, threshold = 100) {
  let deferTimer = null
  let lastCall = null
  return (...args) => {
    const now = timestamp()
    if (lastCall && now < lastCall + threshold) {
      // defer
      if (deferTimer) clearTimeout(deferTimer)
      deferTimer = setTimeout(() => {
        deferTimer = null
        lastCall = now
        fn(...args)
      }, threshold)
    } else {
      if (deferTimer) clearTimeout(deferTimer)
      deferTimer = null
      lastCall = now
      fn(...args)
    }
  }
}

export function DOMchildOf(child, parent) {
  return Boolean(parent && parent.contains(child))
}

export function DOMfindParent(el, checker, maxRec) {
  if (null == el || (null != maxRec && maxRec < 0)) {
    return null
  } else if (checker && checker(el)) {
    return el
  } else {
    return DOMfindParent(el.parentElement || el.parentNode, checker, null == maxRec ? null : maxRec - 1)
  }
}

export function DOMclosest(el, selector) {
  if (null == el || null == selector) {
    return null
  } else if (el.closest) {
    return el.closest(selector)
  } else {
    const matchesFn = el.matches
      ? 'matches'
      : el.webkitMatchesSelector
        ? 'webkitMatchesSelector'
        : el.mozMatchesSelector
          ? 'mozMatchesSelector'
          : el.msMatchesSelector
            ? 'msMatchesSelector'
            : el.oMatchesSelector
              ? 'oMatchesSelector'
              : null
    return matchesFn
      ? DOMfindParent(el, function (el) {
          return el && el[matchesFn] && el[matchesFn](selector)
        })
      : null
  }
}

export function viewport() {
  const element = document.documentElement
  const body = document.querySelector('body')
  return {
    w: window.innerWidth || element.clientWidth || body.clientWidth,
    h: window.innerHeight || element.clientHeight || body.clientHeight
  }
}

export function elementDimensions(element, fast = false) {
  if (!fast && 'function' == typeof window.getComputedStyle) {
    const style = window.getComputedStyle(element)
    const [_w, width, widthUnits] = style.getPropertyValue('width').match(/^([\d.]+)?(.*)$/)
    const [_h, height, heightUnits] = style.getPropertyValue('height').match(/^([\d.]+)?(.*)$/)
    return {
      width: parseFloat(width),
      widthUnits,
      height: parseFloat(height),
      heightUnits
    }
  } else {
    return {
      width: element.offsetWidth,
      widthUnits: 'px',
      height: element.offsetHeight,
      heightUnits: 'px'
    }
  }
}

export function getPageSize() {
  return {
    w: Math.max(
      document.body.scrollWidth,
      document.body.offsetWidth,
      document.documentElement.clientWidth,
      document.documentElement.scrollWidth,
      document.documentElement.offsetWidth
    ),
    h: Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.clientHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    )
  }
}

export function getRoot() {
  return document.querySelectorAll('[data-reactroot]')[0]
}

export function formatCurrency(amount, cents = true) {
  return cents
    ? amount.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,')
    : `${amount.toString()}.`.replace(/(\d)(?=(\d{3})+\.)/g, '$1,').slice(0, -1)
}

export function formatCurrencyUsd(cents) {
  const usd = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
  return usd.format(cents / 100)
}

export function filterCurrencyStr(t = '') {
  const d = t.split('.', 2)
  return `${d[0].replace(/[^\d,]+/g, '')}${null != d[1] ? '.' + d[1].replace(/\D+/g, '').slice(0, 2) : ''}`
}

export function formatCurrencyStr(t = '') {
  return '' == t ? '' : formatCurrency(parseFloat(filterCurrencyStr(t).replace(/,/g, '') || 0))
}

export function filterNumberStr(t = '') {
  return `${t.replace(/[^\d,]+/g, '')}`
}

export function formatNumberStr(t = '') {
  return formatCurrency(parseFloat(filterNumberStr(t).replace(/,/g, '') || 0), false)
}

export function listPipe(list, data, skipNulls = false) {
  return list.reduce((d, f) => {
    const nextD = f(d)
    return skipNulls ? (null == nextD ? d : nextD) : nextD
  }, data)
}

export function listPipeFind(list, defaultValue) {
  return list.reduce((d, f) => (null == d ? f() : d), null) || defaultValue
}

export function updateImmutableIfNeeded(i, id, v) {
  return i.get(id) == v ? i : i.set(id, v)
}

export function padRight(value, size, filler = ' ') {
  const vStr = String(value)
  if (vStr.length >= size) {
    return vStr
  }
  return Array.from({ length: size - vStr.length + 1 }).join(filler) + vStr
}

export function trimRight(str, reSpaceChars = '\\s\uFEFF\u00A0') {
  return str.replace(new RegExp(`[${reSpaceChars}]+$`, 'g'), '')
}

export function isKeyUndo(event) {
  // Ctrl+z || Meta+z || Alt+Backspace
  return (
    (event.ctrlKey && !event.shiftKey && !event.altKey && ('z' == event.key || KEYCODE_Z == event.keyCode)) ||
    (event.metaKey && !event.shiftKey && !event.altKey && ('z' == event.key || KEYCODE_Z == event.keyCode)) ||
    (event.altKey &&
      !event.shiftKey &&
      !event.ctrlKey &&
      ('Backspace' == event.key || KEYCODE_BACKSPACE == event.keyCode))
  )
}

export function isKeyRedo(event) {
  // Ctrl+y || Meta+Shift+z || Alt+Shift+Backspace
  return (
    (event.ctrlKey && !event.shiftKey && !event.altKey && ('y' == event.key || KEYCODE_Y == event.keyCode)) ||
    (event.metaKey && event.shiftKey && !event.altKey && ('Z' == event.key || KEYCODE_Z == event.keyCode)) ||
    (event.altKey &&
      event.shiftKey &&
      !event.ctrlKey &&
      ('Backspace' == event.key || KEYCODE_BACKSPACE == event.keyCode))
  )
}

export function randomInt(from = 0, to = 10) {
  return from + Math.floor(Math.random() * (to - from + 1))
}

export class Growl {
  static error = error => {
    const er = {
      class_name: 'error',
      time: 100000000000,
      text: error.text,
      title: error.title ? `<i class='icon-aytmfd-sad-emoticon'></i>${error.title}` : ''
    }
    return $.gritter.add(er)
  }
  static info = info => {
    const msg = {
      class_name: 'info',
      time: 100000000000,
      text: info.text,
      title: info.title ? `<i class='icon-aytmfd-happy-emoticon'></i>${info.title}` : ''
    }
    return $.gritter.add(msg)
  }
  static hideAll = () => {
    $.gritter.removeAll()
  }
  static hide = index => {
    $.gritter.remove(index)
  }
  static showError = callback => {
    const id = Growl.error({
      title: 'Sorry, but something went wrong',
      text: "Please try again later or <a href='javascript:window.runReactCallback()'>retry</a> now"
    })
    window.runReactCallback = () => {
      Growl.hide(id)
      'function' == typeof callback && callback()
    }
    return id
  }
}

export function getSelection(inputEl) {
  if (null != inputEl) {
    if (null != inputEl.selectionStart) {
      return {
        start: inputEl.selectionStart,
        end: inputEl.selectionEnd
      }
    } else if (null != document.selection) {
      const range = document.selection.createRange()
      if (range.parentElement() == inputEl) {
        return {
          start: -range.moveStart('character', -inputEl.value.length),
          end: -range.moveEnd('character', -inputEl.value.length)
        }
      }
    }
  }
  return {
    start: 0,
    end: 0
  }
}

export function setSelection(inputEl, selection = {}) {
  const selectionStart = selection.start || selection.selectionStart || 0
  const selectionEnd = selection.end || selection.selectionEnd || selectionStart
  if (null != inputEl) {
    if (null != inputEl.selectionStart) {
      inputEl.selectionStart = selectionStart
      inputEl.selectionEnd = selectionEnd
    } else if (null != inputEl.setSelectionRange) {
      inputEl.setSelectionRange(selectionStart, selectionEnd)
    } else if (null != inputEl.createTextRange) {
      const range = inputEl.createTextRange()
      range.collapse(true)
      range.moveStart('character', selectionStart)
      range.moveEnd('character', selectionEnd)
      range.select()
    }
  }
}

function modifyCardName(text) {
  return text.toLowerCase().replace(/\s+/g, '')
}
export function preprocessText(text = '') {
  return !(text && 'function' == typeof text.replace)
    ? text
    : text
        .replace(
          /<credit-card type=["']([^<]*)["']\/>/g,
          '<img src="/images/subscriptions/$1.png" class="credit-card $1" alt="subscription credit card" />'
        )
        .replace(RegExp.$1, modifyCardName(RegExp.$1))
        .replace(
          /<credit-card type=["']([^<]*)["']><\/credit-card>/g,
          '<img src="/images/subscriptions/$1.png" class="credit-card $1" alt="subscription credit card" />'
        )
        .replace(RegExp.$1, modifyCardName(RegExp.$1))
        .replace(/<err>(.*?)<\/err>/g, "<span style='color:#F15A24'>$1</span>")
        .replace(/<warn>(.*?)<\/warn>/g, "<span style='color:#DD9217'>$1</span>")
}

export function escapeText(text = '', br = false) {
  return !(text && 'function' == typeof text.replace)
    ? text
    : text
        .replace(/&lt;b&gt;/gi, '<b>')
        .replace(/&lt;\/b&gt;/gi, '</b>')
        .replace(/&lt;bold&gt;/gi, '<b>')
        .replace(/&lt;\/bold&gt;/gi, '</b>')
        .replace(/&lt;i&gt;/gi, '<i>')
        .replace(/&lt;\/i&gt;/gi, '</i>')
        .replace(/&lt;br\s*\/&gt;/gi, br ? '<br/>' : '')
        .replace(/&lt;br\s*&gt;/gi, br ? '<br/>' : '')
}

const ESCAPE_HTML_MAP = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  '`': '&#x60;',
  "'": '&#39;',
  [String.fromCharCode(160)]: ' ' // convert &nbsp; to spaces
}
const ESCAPE_TAGS_MAP = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  [String.fromCharCode(160)]: ' ' // convert &nbsp; to spaces
}
const ESCAPE_HTML_REGEXP_TEST = new RegExp(`(?:${Object.keys(ESCAPE_HTML_MAP).join('|')})`)
const ESCAPE_TAGS_REGEXP_TEST = new RegExp(`(?:${Object.keys(ESCAPE_TAGS_MAP).join('|')})`)
const ESCAPE_HTML_REGEXP = new RegExp(`(?:${Object.keys(ESCAPE_HTML_MAP).join('|')})`, 'g')
const ESCAPE_TAGS_REGEXP = new RegExp(`(?:${Object.keys(ESCAPE_TAGS_MAP).join('|')})`, 'g')
export function escapeHTML(html) {
  return ESCAPE_HTML_REGEXP_TEST.test(html) ? html.replace(ESCAPE_HTML_REGEXP, match => ESCAPE_HTML_MAP[match]) : html
}
export function escapeTags(html) {
  return ESCAPE_TAGS_REGEXP_TEST.test(html) ? html.replace(ESCAPE_TAGS_REGEXP, match => ESCAPE_TAGS_MAP[match]) : html
}
const UNESCAPE_HTML_MAP = Object.keys(ESCAPE_HTML_MAP).reduce((map, key) => ((map[ESCAPE_HTML_MAP[key]] = key), map), {
  '&nbsp;': ' ' // convert &nbsp; to spaces
})
delete UNESCAPE_HTML_MAP[' '] // remove space to &nbsp; conversion
const UNESCAPE_TAGS_MAP = Object.keys(ESCAPE_TAGS_MAP).reduce((map, key) => ((map[ESCAPE_HTML_MAP[key]] = key), map), {
  '&nbsp;': ' ' // convert &nbsp; to spaces
})
delete UNESCAPE_TAGS_MAP[' '] // remove space to &nbsp; conversion
const UNESCAPE_HTML_REGEXP_TEST = new RegExp(`(?:${Object.keys(UNESCAPE_HTML_MAP).join('|')})`)
const UNESCAPE_TAGS_REGEXP_TEST = new RegExp(`(?:${Object.keys(UNESCAPE_TAGS_MAP).join('|')})`)
const UNESCAPE_HTML_REGEXP = new RegExp(`(?:${Object.keys(UNESCAPE_HTML_MAP).join('|')})`, 'g')
const UNESCAPE_TAGS_REGEXP = new RegExp(`(?:${Object.keys(UNESCAPE_TAGS_MAP).join('|')})`, 'g')
export function unescapeHTML(html) {
  return UNESCAPE_HTML_REGEXP_TEST.test(html)
    ? html.replace(UNESCAPE_HTML_REGEXP, match => UNESCAPE_HTML_MAP[match])
    : html
}
export function unescapeTags(html) {
  return UNESCAPE_TAGS_REGEXP_TEST.test(html)
    ? html.replace(UNESCAPE_TAGS_REGEXP, match => UNESCAPE_TAGS_MAP[match])
    : html
}

export function escapeRegExp(str) {
  return str.replace(/[$()*+.?[\\\]^{|}]/g, '\\$&')
}

export function stripHTML(html) {
  return html.replace(/(<([^>]+)>)/gi, '')
}

export function secondsToMMSS(seconds) {
  if (Number.isInteger(seconds) && seconds >= 0) {
    const measuredTime = new Date(null)
    measuredTime.setSeconds(seconds)
    return measuredTime.toISOString().slice(14, 19)
  }
  return null
}

//TODO
export function touchMapper(event) {
  stopEvent(event)
  const touches = event.changedTouches
  const firstTouch = touches && touches.length ? touches[0] : {}
  const type = (() => {
    switch (event.type) {
      case 'touchstart':
        return 'mousedown'
      case 'touchmove':
        return 'mousemove'
      case 'touchend':
        return 'mouseup'
      case 'touchcancel':
        return 'mouseup'
      default:
        return null
    }
  })()
  if (type && firstTouch) {
    const mouseEvent = document.createEvent('MouseEvent')
    mouseEvent.initMouseEvent(
      type,
      true,
      true,
      window,
      1,
      firstTouch.screenX,
      firstTouch.screenY,
      firstTouch.clientX,
      firstTouch.clientY,
      false,
      false,
      false,
      false,
      0,
      null
    )
    firstTouch.target.dispatchEvent(mouseEvent)
  }
  return false
}

export function dragContainsFiles(event) {
  if (!event || !event.dataTransfer || !Array.isArray(event.dataTransfer.types)) {
    return false
  }
  for (const t of event.dataTransfer.types) {
    if (0 <= (t || '').search(/file/i)) return true
  }
  return false
}

export const getPrototype = 'function' == typeof Object.getPrototypeOf ? Object.getPrototypeOf : o => o.__proto__

export function superProp(TargetClass, propKey, checkSelf = false) {
  if ('function' == typeof TargetClass && null != propKey) {
    if (checkSelf && void 0 !== TargetClass.prototype && void 0 !== TargetClass.prototype[propKey])
      return TargetClass.prototype[propKey]
    let prototype = getPrototype(TargetClass)
    while (null != prototype) {
      if (void 0 !== prototype[propKey]) {
        return prototype[propKey]
      } else {
        prototype = getPrototype(prototype)
      }
    }
  }
  return void 0
}

export function DOMElementRect(element, absolute = true) {
  if (null == element) return {}
  const rect = element.getBoundingClientRect()
  const fixedRect = {}
  fixedRect.top = rect.top || rect.y
  fixedRect.left = rect.left || rect.x
  fixedRect.bottom = rect.bottom
  fixedRect.right = rect.right
  fixedRect.width = rect.width || rect.right - rect.left
  fixedRect.height = rect.height || rect.bottom - rect.top
  fixedRect.x = rect.x || rect.left
  fixedRect.y = rect.y || rect.top
  if (absolute) {
    const scrollXOffset = scrollX()
    const scrollYOffset = scrollY()
    fixedRect.top = fixedRect.top + scrollYOffset
    fixedRect.left = fixedRect.left + scrollXOffset
    fixedRect.bottom = fixedRect.bottom + scrollYOffset
    fixedRect.right = fixedRect.right + scrollXOffset
    fixedRect.x = fixedRect.x + scrollXOffset
    fixedRect.y = fixedRect.y + scrollYOffset
  }
  return fixedRect
}

export const nth_en = (function () {
  const below20 = [
    'Zeroth',
    'First',
    'Second',
    'Third',
    'Fourth',
    'Fifth',
    'Sixth',
    'Seventh',
    'Eighth',
    'Ninth',
    'Tenth',
    'Eleventh',
    'Twelfth',
    'Thirteenth',
    'Fourteenth',
    'Fifteenth',
    'Sixteenth',
    'Seventeenth',
    'Eighteenth',
    'Nineteenth'
  ]
  const decaPrefix = ['Twent', 'Thirt', 'Fort', 'Fift', 'Sixt', 'Sevent', 'Eight', 'Ninet']
  return number =>
    20 > number
      ? below20[number]
      : 0 == number % 10 && 100 > number
        ? `${decaPrefix[number / 10 - 2]}ieth`
        : 100 > number
          ? `${decaPrefix[Math.floor(number / 10) - 2]}y ${below20[number % 10]}`
          : 100 == number
            ? 'Hundredth'
            : 'Another'
})()

export function trimListItemText(text) {
  return (text || '').replace(/^\s*([*§·Øü”•‣⁃⁌⁍○◘⦾⦿�]|\d+\.)\s*/, '').trim()
}

export function capitalize(str) {
  return str.slice(0, 1).toUpperCase() + str.slice(1)
}

export function divideLongWords(string, maxLength = 100) {
  return string
    .split(' ')
    .map(word => (maxLength > word.length ? word : word.match(new RegExp(`.{1,${maxLength}}`, 'g')).join(' ')))
    .join(' ')
}

export function nextFocusableElement(
  thisElement,
  selector = 'a[href], button:not([disabled]), area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, object, embed, *[tabindex]:not([tabindex="-1"]), *[contenteditable]'
) {
  const documentFocusable = document.querySelectorAll(selector)
  for (let i = 0; i < documentFocusable.length; i++) {
    if (documentFocusable[i] === thisElement) {
      return documentFocusable[i + 1]
    }
  }
  return void 0
}

export function navigateToExternalUrl(url, shouldOpenNewTab = true) {
  shouldOpenNewTab ? window.open(url, '_blank') : (window.location.href = url)
}
